import React, { useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import Layout from '../components/Layout.jsx';

const RATINGS = [1, 2, 3, 4, 5];

function StarRating({ describedBy, error, onChange, value }) {
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-lg p-1 ${error ? 'ring-1 ring-red-300' : ''}`}
      aria-describedby={describedBy}
    >
      {RATINGS.map((n) => (
        <label key={n} className="cursor-pointer">
          <input
            id={`overall_rating_${n}`}
            type="radio"
            name="overall_rating"
            value={n}
            checked={value === n}
            onChange={() => onChange(n)}
            className="sr-only peer"
          />
          <span
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            className={`block rounded-md p-1 text-3xl leading-none transition peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 ${
              value !== null && n <= value ? 'text-amber-400' : 'text-gray-300 hover:text-amber-200'
            }`}
          >
            ★
          </span>
        </label>
      ))}
      <span className="ml-3 text-sm text-gray-500">
        {value === null ? 'No rating selected' : `${value} of 5`}
      </span>
    </div>
  );
}

function ScaleRating({ describedBy, error, highLabel, lowLabel, name, onChange, value }) {
  return (
    <div className={`rounded-lg ${error ? 'ring-1 ring-red-300 p-2' : ''}`} aria-describedby={describedBy}>
      <div className="flex flex-wrap items-center gap-2">
        {RATINGS.map((n) => (
          <label key={n} className="cursor-pointer">
            <input
              id={`${name}_${n}`}
              type="radio"
              name={name}
              value={n}
              checked={value === n}
              onChange={() => onChange(n)}
              className="sr-only peer"
            />
            <span
              aria-label={`${n}${n === 1 ? ` — ${lowLabel}` : ''}${n === 5 ? ` — ${highLabel}` : ''}`}
              className={`flex h-11 w-11 items-center justify-center rounded-lg border text-sm font-semibold transition peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 ${
                value === n
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300'
              }`}
            >
              {n}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-xs text-gray-500 max-w-[19rem]">
        <span>1 — {lowLabel}</span>
        <span>5 — {highLabel}</span>
      </div>
    </div>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-sm font-medium text-red-600">
      {message}
    </p>
  );
}

export default function WebinarEvaluation({
  certificate_available: certificateAvailable = true,
  certificate_url: certificateUrl,
  completed = false,
  completed_on: completedOn,
  course_title: courseTitle,
  credit_hours: creditHours,
  errors = {},
  ineligible_reason: ineligibleReason,
  submit_url: submitUrl,
  submitted_values: submittedValues = {},
}) {
  const { flash = {} } = usePage().props;

  const [overallRating, setOverallRating] = useState(submittedValues.overall_rating ?? null);
  const [ratingReason, setRatingReason] = useState(submittedValues.rating_reason ?? '');
  const [improvements, setImprovements] = useState(submittedValues.improvement_suggestions ?? '');
  const [metObjectives, setMetObjectives] = useState(
    typeof submittedValues.met_objectives === 'boolean' ? submittedValues.met_objectives : null,
  );
  const [instructorRating, setInstructorRating] = useState(
    submittedValues.instructor_knowledge_rating ?? null,
  );
  const [contentRating, setContentRating] = useState(submittedValues.content_quality_rating ?? null);
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const missing = {
    overall_rating: overallRating === null ? 'Choose a rating from 1 to 5 stars.' : null,
    rating_reason: ratingReason.trim() ? null : 'Tell us why you chose that rating.',
    improvement_suggestions: improvements.trim() ? null : 'Tell us what you would like improved.',
    met_objectives: metObjectives === null ? 'Choose Yes or No.' : null,
    instructor_knowledge_rating: instructorRating === null ? 'Choose a value from 1 to 5.' : null,
    content_quality_rating: contentRating === null ? 'Choose a value from 1 to 5.' : null,
  };
  const incomplete = Object.values(missing).some(Boolean);
  const shownError = (field) => (attempted ? missing[field] : null) || errors[field] || null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttempted(true);
    if (incomplete || submitting) return;

    setSubmitting(true);
    router.post(
      submitUrl,
      {
        evaluation: {
          content_quality_rating: contentRating,
          improvement_suggestions: improvements.trim(),
          instructor_knowledge_rating: instructorRating,
          met_objectives: metObjectives,
          overall_rating: overallRating,
          rating_reason: ratingReason.trim(),
        },
      },
      { onFinish: () => setSubmitting(false) },
    );
  };

  const header = (
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">
        Course evaluation
      </h1>
      <p className="mt-1 text-sm text-gray-600">
        {courseTitle}
        {completedOn ? ` · ${completedOn}` : ''}
        {creditHours != null ? ` · ${creditHours} course credits` : ''}
      </p>
    </div>
  );

  if (ineligibleReason) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          {header}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
            {ineligibleReason}
          </div>
        </div>
      </Layout>
    );
  }

  if (completed) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          {header}
          {flash.alert && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {flash.alert}
            </div>
          )}
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-semibold text-gray-900">Evaluation complete</h2>
            <p className="mt-1 text-sm text-gray-600">
              {certificateAvailable
                ? 'Thanks for the feedback. Your certificate of completion is ready.'
                : 'Thanks for the feedback. A certificate is not available for this registration.'}
            </p>
            {certificateAvailable && (
              <a
                href={certificateUrl}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download your certificate
              </a>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {header}

        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900">
          Every question below is required. Your certificate becomes available as soon as the
          evaluation is submitted.
        </div>

        {flash.alert && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {flash.alert}
          </div>
        )}

        {attempted && incomplete && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Please answer every question before submitting.
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-8 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
        >
          <fieldset>
            <legend className="text-sm font-semibold text-gray-900">
              Overall course rating <span className="text-red-600">*</span>
            </legend>
            <p className="mt-1 text-sm text-gray-500">How would you rate this course overall?</p>
            <div className="mt-2">
              <StarRating
                describedBy="overall_rating_error"
                error={Boolean(shownError('overall_rating'))}
                onChange={setOverallRating}
                value={overallRating}
              />
            </div>
            <FieldError id="overall_rating_error" message={shownError('overall_rating')} />
          </fieldset>

          <div>
            <label htmlFor="rating_reason" className="block text-sm font-semibold text-gray-900">
              Why did you choose that rating? <span className="text-red-600">*</span>
            </label>
            <textarea
              id="rating_reason"
              rows={4}
              maxLength={2000}
              value={ratingReason}
              onChange={(e) => setRatingReason(e.target.value)}
              aria-describedby="rating_reason_error"
              aria-invalid={Boolean(shownError('rating_reason'))}
              className="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            <FieldError id="rating_reason_error" message={shownError('rating_reason')} />
          </div>

          <div>
            <label htmlFor="improvement_suggestions" className="block text-sm font-semibold text-gray-900">
              What improvements would you like to see? <span className="text-red-600">*</span>
            </label>
            <textarea
              id="improvement_suggestions"
              rows={4}
              maxLength={2000}
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              aria-describedby="improvement_suggestions_error"
              aria-invalid={Boolean(shownError('improvement_suggestions'))}
              className="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            <FieldError id="improvement_suggestions_error" message={shownError('improvement_suggestions')} />
          </div>

          <fieldset>
            <legend className="text-sm font-semibold text-gray-900">
              Did the course help achieve its learning objectives? <span className="text-red-600">*</span>
            </legend>
            <div className="mt-2 flex gap-3" aria-describedby="met_objectives_error">
              {[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ].map((option) => (
                <label key={option.label} className="cursor-pointer">
                  <input
                    id={`met_objectives_${option.value}`}
                    type="radio"
                    name="met_objectives"
                    value={String(option.value)}
                    checked={metObjectives === option.value}
                    onChange={() => setMetObjectives(option.value)}
                    className="sr-only peer"
                  />
                  <span
                    className={`flex h-11 min-w-[5.5rem] items-center justify-center rounded-lg border px-4 text-sm font-semibold transition peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 ${
                      metObjectives === option.value
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300'
                    }`}
                  >
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            <FieldError id="met_objectives_error" message={shownError('met_objectives')} />
          </fieldset>

          <fieldset>
            <legend className="text-sm font-semibold text-gray-900">
              Instructor knowledge <span className="text-red-600">*</span>
            </legend>
            <p className="mt-1 text-sm text-gray-500">
              The instructor was knowledgeable about the subject matter.
            </p>
            <div className="mt-2">
              <ScaleRating
                describedBy="instructor_knowledge_rating_error"
                error={Boolean(shownError('instructor_knowledge_rating'))}
                highLabel="Strongly Agree"
                lowLabel="Strongly Disagree"
                name="instructor_knowledge_rating"
                onChange={setInstructorRating}
                value={instructorRating}
              />
            </div>
            <FieldError
              id="instructor_knowledge_rating_error"
              message={shownError('instructor_knowledge_rating')}
            />
          </fieldset>

          <fieldset>
            <legend className="text-sm font-semibold text-gray-900">
              Overall content quality <span className="text-red-600">*</span>
            </legend>
            <div className="mt-2">
              <ScaleRating
                describedBy="content_quality_rating_error"
                error={Boolean(shownError('content_quality_rating'))}
                highLabel="Excellent"
                lowLabel="Poor"
                name="content_quality_rating"
                onChange={setContentRating}
                value={contentRating}
              />
            </div>
            <FieldError id="content_quality_rating_error" message={shownError('content_quality_rating')} />
          </fieldset>

          <div className="flex justify-end border-t border-gray-100 pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Submit evaluation'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
